import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import PasswordInput from '../../components/ui/password-input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import TurnstileWidget from '../../components/shared/TurnstileWidget';

// Captcha aktiv, sobald ein Turnstile-Sitekey gesetzt ist.
const TURNSTILE_ENABLED = !!process.env.REACT_APP_TURNSTILE_SITEKEY;

export const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/markets');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const success = await login(email, password);
    if (success) {
      navigate('/markets');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen -mt-[61px] pt-[61px] bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl dark:bg-slate-900/80 dark:border-slate-800 dark:backdrop-blur-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-200 dark:to-white bg-clip-text text-transparent pb-1">
            {t('login.welcome')}
          </CardTitle>
          <CardDescription className="dark:text-gray-400">{t('login.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="dark:text-gray-300">{t('login.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="dark:text-gray-300">{t('login.password')}</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? t('common.loading') : t('login.submit')}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">
              {t('login.createAccount')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export const RegisterPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    handle: '',
    email: '',
    password: '',
    confirmPassword: '',
    leaderboard_visible: false,
    acceptAgb: false,
    acceptDatenschutz: false,
    confirmAge: false
  });

  const allConsentsGiven = formData.acceptAgb && formData.acceptDatenschutz && formData.confirmAge;
  const [loading, setLoading] = useState(false);
  const [handleStatus, setHandleStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [captchaToken, setCaptchaToken] = useState('');
  const { register, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) navigate('/markets');
  }, [user, navigate]);

  // Debounced handle availability check
  useEffect(() => {
    if (!formData.handle || formData.handle.length < 2) {
      setHandleStatus(null);
      return;
    }
    setHandleStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API_URL}/api/auth/check-handle?handle=${encodeURIComponent(formData.handle)}`);
        const data = await res.json();
        setHandleStatus(data.available ? 'available' : 'taken');
      } catch {
        setHandleStatus(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [formData.handle]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword || handleStatus === 'taken') return;
    // Client-seitige Mindestlaenge (Server erzwingt dasselbe verbindlich).
    if (formData.password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setLoading(true);
    try {
      // Referral-Code aus localStorage (vom ReferralTracker gesetzt, wenn
      // der User ueber einen ?ref=-Link kam) mitgeben.
      let refCode = null;
      try { refCode = localStorage.getItem('ref_code'); } catch { /* ignore */ }
      const success = await register(
        formData.handle,
        formData.email,
        formData.password,
        refCode || null,
        formData.leaderboard_visible,
        captchaToken || null
      );
      if (success) {
        try { localStorage.removeItem('ref_code'); } catch { /* ignore */ }
        navigate('/markets');
      }
    } catch (err) {
      // Server-Validierung (400) + Uniqueness-Fehler sichtbar machen --
      // bisher wurde der Fehler verschluckt und der Button blieb haengen.
      toast.error(err?.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen -mt-[61px] pt-[61px] bg-gradient-to-br from-sky-50 to-blue-50 dark:from-black dark:via-blue-950 dark:to-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl dark:bg-slate-900/80 dark:border-slate-800 dark:backdrop-blur-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl bg-gradient-to-r from-blue-700 to-blue-900 dark:from-blue-200 dark:to-white bg-clip-text text-transparent pb-1">
            {t('signup.title')}
          </CardTitle>
          <CardDescription className="dark:text-gray-400">{t('signup.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="handle" className="dark:text-gray-300">{t('signup.handle')} *</Label>
              <div className="relative">
                <Input
                  id="handle"
                  type="text"
                  placeholder="Dein öffentlicher Anzeigename"
                  value={formData.handle}
                  onChange={(e) => setFormData({...formData, handle: e.target.value})}
                  required
                  minLength={2}
                  data-testid="register-handle-input"
                  className={handleStatus === 'taken' ? 'border-rose-400 dark:border-rose-500/60' : handleStatus === 'available' ? 'border-emerald-400 dark:border-emerald-500/60' : ''}
                />
                {handleStatus === 'checking' && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 dark:text-gray-500">...</span>
                )}
                {handleStatus === 'available' && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500 dark:text-emerald-400 text-sm">&#10003;</span>
                )}
                {handleStatus === 'taken' && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-rose-500 dark:text-rose-400 text-sm">&#10007;</span>
                )}
              </div>
              {handleStatus === 'taken' && (
                <p className="text-xs text-rose-500 dark:text-rose-400">Username ist bereits vergeben</p>
              )}
              <p className="text-xs text-gray-400 dark:text-gray-500">Dieser Name erscheint auf dem Leaderboard</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-email" className="dark:text-gray-300">{t('signup.email')}</Label>
              <Input
                id="reg-email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reg-password" className="dark:text-gray-300">{t('signup.password')}</Label>
              <PasswordInput
                id="reg-password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                data-testid="register-password-input"
              />
              {/* Passwort-Anforderung vorab + live sichtbar (statt erst bei
                  Fehler) — wird grün mit Haken, sobald erfüllt. */}
              <p
                className={`text-xs flex items-center gap-1.5 ${
                  formData.password.length >= 8
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
                data-testid="register-password-hint"
              >
                <span>{formData.password.length >= 8 ? '✓' : '•'}</span>
                Mindestens 8 Zeichen
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="dark:text-gray-300">Passwort bestätigen</Label>
              <PasswordInput
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                required
                data-testid="register-confirm-password-input"
              />
              {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-xs text-rose-500 dark:text-rose-400">Passwörter stimmen nicht überein</p>
              )}
            </div>

            {/* Leaderboard Consent */}
            <div className="p-3 bg-gray-50 dark:bg-slate-800/60 rounded-lg border border-gray-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Auf dem Leaderboard erscheinen?</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Dein Username wird öffentlich im Ranking angezeigt</p>
                </div>
                <button
                  type="button"
                  data-testid="register-leaderboard-toggle"
                  onClick={() => setFormData({...formData, leaderboard_visible: !formData.leaderboard_visible})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.leaderboard_visible
                      ? 'bg-emerald-500 dark:bg-emerald-500'
                      : 'bg-gray-300 dark:bg-slate-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.leaderboard_visible ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Pflicht-Checkboxen */}
            <div className="space-y-3 pt-1">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="register-agb-label">
                <input
                  type="checkbox"
                  checked={formData.acceptAgb}
                  onChange={(e) => setFormData({...formData, acceptAgb: e.target.checked})}
                  className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-slate-600 dark:bg-slate-800 text-emerald-600 focus:ring-emerald-500"
                  data-testid="register-agb-checkbox"
                />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Ich akzeptiere die{' '}
                  <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-emerald-600 dark:text-emerald-400 underline hover:text-emerald-700 dark:hover:text-emerald-300">
                    AGB
                  </a>
                  {' '}*
                </span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer" data-testid="register-datenschutz-label">
                <input
                  type="checkbox"
                  checked={formData.acceptDatenschutz}
                  onChange={(e) => setFormData({...formData, acceptDatenschutz: e.target.checked})}
                  className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-slate-600 dark:bg-slate-800 text-emerald-600 focus:ring-emerald-500"
                  data-testid="register-datenschutz-checkbox"
                />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Ich habe die{' '}
                  <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-emerald-600 dark:text-emerald-400 underline hover:text-emerald-700 dark:hover:text-emerald-300">
                    Datenschutzerklärung
                  </a>
                  {' '}gelesen und akzeptiere sie *
                </span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer" data-testid="register-age-label">
                <input
                  type="checkbox"
                  checked={formData.confirmAge}
                  onChange={(e) => setFormData({...formData, confirmAge: e.target.checked})}
                  className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-slate-600 dark:bg-slate-800 text-emerald-600 focus:ring-emerald-500"
                  data-testid="register-age-checkbox"
                />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Ich bestätige, dass ich mindestens 18 Jahre alt bin *
                </span>
              </label>
            </div>

            {/* Captcha — rendert nur, wenn REACT_APP_TURNSTILE_SITEKEY gesetzt ist */}
            <TurnstileWidget onToken={setCaptchaToken} />

            <Button
              type="submit"
              className="w-full"
              disabled={loading || formData.password !== formData.confirmPassword || handleStatus === 'taken' || !allConsentsGiven || (TURNSTILE_ENABLED && !captchaToken)}
              data-testid="register-submit-btn"
            >
              {loading ? 'Konto wird erstellt...' : 'Konto erstellen'}
            </Button>
          </form>

          <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
            {t('signup.haveAccount')}{' '}
            <Link to="/login" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">
              {t('signup.loginLink')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
